import { OT_MULTIPLIER, toOplusTime, yearMonthDateString } from "../common/common";
import { roundTwoDecimals, roundNum } from "./common";

// summary_common includes methods used by summary, summary dws, and print pages.
// Take caution when making changes. If all pages don't support the change, it will likely cause bugs.

// renderAllStatsColumns will use jQuery to render cell stats in the column totals.
export function renderAllStatsColumns(tableDates, summaryStats, roleStore, roleData, overtimeStartTime, canViewSpotWorker = false) {
  tableDates.forEach(d => {
    const dateKey = d.index()
    const date = d.getDate();
    let column = {
      totalCost: 0,
      laborHours: 0,
      transportCost: 0,
      laborCost: 0,
      numOfWorkers: 0,
      roleCounts: {},
      orRoleCounts: {},
      roleSectionCounts: {},
      otLaborHours: 0,
      otLaborCost: 0,
    }

    column = summaryStats.getColumnStats(dateKey).getData();
    const stat = summaryStats.getSpotWorkColumnStats(dateKey)

    if (canViewSpotWorker && stat) {
      column = stat
    }

    renderStatsColumn(toOplusTime(date), column, roleStore, roleData, overtimeStartTime);
  })
}

/**
 * Renders a stats column for a given date into the DOM.
 * @param {Date} date - The date for this column.
 * @param {import('../stats/shiftColumnStats').ShiftColumnStats} column - Stats data for the column.
 * @param {import('../store/v1/roleStore').RoleStore} roleStore - Store for looking up role metadata.
 * @param {import('../app/summaryTable').RoleCountData} roleData - Role count requirements by role and date.
 * @param {boolean} overtimeStartTime - Whether OT cost should be included in the total cost.
 */
function renderStatsColumn(date, column, roleStore, roleData, overtimeStartTime) {
  // Element IDs currently use the timestamps from the Go template, which are in seconds so we convert JS-standard ms to seconds.
  const selectorTimestamp = yearMonthDateString(date, "UTC"); // To work with oplus time DOM, hardcode UTC timezone.
  $(`#num-of-workers-${selectorTimestamp}`).text(column.numOfWorkers).toggleClass("text-gray", !column.numOfWorkers);
  $(`#labor-hours-${selectorTimestamp}`).text(roundTwoDecimals(column.laborHours, ",")).toggleClass("text-gray", !column.laborHours);
  $(`#ot-hours-${selectorTimestamp}`).text(roundTwoDecimals(column.otLaborHours, ",")).toggleClass("text-gray", !column.otLaborHours);

  // business requirement, OT cost is multiplied by OT_MULTIPLIER (0.25)
  const otCost = column.otLaborCost * OT_MULTIPLIER;
  $(`#ot-cost-${selectorTimestamp}`).text(`${roundNum(otCost, ",")}`).toggleClass("text-gray", !otCost);
  $(`#labor-cost-${selectorTimestamp}`).text(`${roundNum(column.laborCost, ",")}`).toggleClass("text-gray", !column.laborCost);
  $(`#transport-cost-${selectorTimestamp}`).text(`${roundNum(column.transportCost, ",")}`).toggleClass("text-gray", !column.transportCost);
  const totalCost = column.transportCost + column.laborCost + (overtimeStartTime ? otCost : 0);
  $(`#total-cost-${selectorTimestamp}`).text(`${roundNum(totalCost, ",")}`).toggleClass("text-gray", !totalCost);

  for (const roleId in column.roleCounts) {
    const role = roleStore.byId(roleId);
    if (!role || role.isOrRole() || role.isMainRole()) {
      continue;
    }

    let countText = column.roleCounts[roleId];
    const roleCountRequired = getRoleCountByIdAndDate(roleData, roleId, selectorTimestamp)?.Required;
    if (countText === 0 && !roleCountRequired) {
        // If the count is zero and there is no required count set, then we'll set the text back to dash mark instead of zero.
        countText = "-";
    };

    $(`#role-count-${roleId}-${selectorTimestamp}-count`).text(countText);
    setRoleCountTextColor(column.roleCounts[roleId], roleId, selectorTimestamp, `#role-count-${roleId}-${selectorTimestamp}-text`, roleData);
    // setRoleCountTextColor removes all classes. Re-add base class.
    $(`#role-count-${roleId}-${selectorTimestamp}-text`).addClass('stats-cell-text');
  }

  roleStore.query({isMainRole: true, filterMainRole: true}).forEach(r => {
    const roleId = r.getId();
    const onlyRequiresOr = true;
    if (onlyRequiresOr) {
      const currentCount = column.orRoleCounts[roleId] || 0;
      let countText = currentCount.toString();
      const roleCountRequired = getRoleCountByIdAndDate(roleData, roleId, selectorTimestamp)?.Required;
      if (currentCount === 0 && !roleCountRequired) {
          // If the count is zero and there is no required count set, then we'll set the text back to dash mark instead of zero.
          countText = "-";
      };

      $(`#role-count-${roleId}-${selectorTimestamp}-count`).text(countText);
      setRoleCountTextColor(currentCount, roleId, selectorTimestamp, `#role-count-${roleId}-${selectorTimestamp}-text`, roleData);
      // setRoleCountTextColor removes all classes. Re-add base class.
      $(`#role-count-${roleId}-${selectorTimestamp}-text`).addClass('stats-cell-text');
      return;
    }
  })
}

// setRoleCountTextColor will add a spectre text color class to the role count text for the given role and date based on the role's required count and the current count.
export function setRoleCountTextColor(count, roleId, dateString, selector, roleData, requiredCount = null) {
  const outer = $(selector);
  outer.removeClass();
  const roleCountForDate = getRoleCountByIdAndDate(roleData, roleId, dateString);
  let required = roleCountForDate?.Required;
  // Override required count if `requiredCount` param exists, this for roleSection
  if (requiredCount) {
    required = requiredCount
  }
  if (roleCountForDate !== undefined) {
    if (required === 0 && count === 0) {
      outer.addClass('text-gray');
    } else if (count === required) {
      outer.addClass('text-success');
    } else if (count > required) {
      outer.addClass('text-warning');
    } else if (count < required) {
      outer.addClass('text-error');
    }
  }
}

// getRoleCountByIdAndDate will return the role count settings, including required count, for the given role on the given date. Returns an object or undefined.
export function getRoleCountByIdAndDate(roleData, roleId, dateString) {
  return roleData?.rolesCount?.[roleId]?.dates?.[dateString];
}

// renderAllStatsRows will use jQuery to render all stats cells in the user rows.
export function renderAllStatsRows(summaryStats, employmentStore, roleStore, activeBranchId) {
  const employments = employmentStore.query({branchId: activeBranchId});
  employments.forEach(e => {
    const row = summaryStats.getRowStats(e.index()).getData();
    renderStatsRow(e.getUserId(), row, roleStore);
  })
}

function renderStatsRow(userId, row, roleStore) {
  $(`#work-count-${userId}`).text(row.numOfWorkers);
  $(`#labor-hours-${userId}`).text(roundTwoDecimals(row.laborHours));

  for (const offType in row.offCounts) {
    $(`#${offType}-count-${userId}`).text(row.offCounts[offType])
  }

  for (const roleId in row.roleCounts) {
    const role = roleStore.byId(roleId);
    if (!role || role.isOrRole() || role.isMainRole()) {
      continue;
    }
    const countText = row.roleCounts[roleId] || "-";
    $(`#role-count-${roleId}-${userId}-count`).text(countText);
  }

  roleStore.query({isMainRole: true, filterMainRole: true}).forEach(r => {
    const roleId = r.getId();
    const onlyRequiresOr = true;
    if (onlyRequiresOr) {
      const countText = row.orRoleCounts[roleId] || "-";
      $(`#role-count-${roleId}-${userId}-count`).text(countText);
      return;
    }
  })
}
